import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  Users,
  Shield,
  ToggleLeft,
  Sparkles,
  BarChart2,
  ChevronDown,
  LogOut,
  type LucideIcon,
} from "lucide-react";

import { useAuthContext } from "../../auth/AuthContext";
import { useDarkMode } from "../../theme/useDarkMode";
import { trackEvent } from "../../telemetry/appInsights";

// A nav entry is either a single link (leaf) or a collapsible group whose
// children are links. Groups have no route of their own — clicking the header
// only expands/collapses.
type NavLeaf = { to: string; label: string; icon: LucideIcon };
type NavGroup = {
  label: string;
  icon: LucideIcon;
  children: { to: string; label: string }[];
};
type NavItem = NavLeaf | NavGroup;

const isGroup = (item: NavItem): item is NavGroup => "children" in item;

const NAV_ITEMS: NavItem[] = [
  { to: "/users", label: "Users", icon: Users },
  { to: "/personas", label: "Personas", icon: Shield },
  { to: "/feature-flags", label: "Feature Flags", icon: ToggleLeft },
  {
    label: "AI Summary",
    icon: Sparkles,
    children: [
      { to: "/ai-templates", label: "Templates" },
      { to: "/ai-summary", label: "Config" },
      // The group is still labelled "AI Summary" but now holds visit notes too.
      // Renaming it to "AI" would be more accurate; left alone deliberately —
      // it changes a label admins already navigate by, so it needs a product OK.
      { to: "/visit-notes", label: "Visit Notes" },
      { to: "/clinical-templates", label: "Clinical Templates" },
    ],
  },
  { to: "/usage", label: "Usage", icon: BarChart2 },
];

const leafLinkClass = ({ isActive }: { isActive: boolean }) =>
  "flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors " +
  (isActive
    ? "bg-primary/10 text-primary"
    : "text-foreground hover:bg-accent hover:text-accent-foreground");

/**
 * Collapsible nav group. Starts expanded when the current route is one of its
 * children so the active submenu is always visible after a reload/deep-link.
 */
function NavGroupSection({ group }: { group: NavGroup }) {
  const location = useLocation();
  const hasActiveChild = group.children.some((c) =>
    location.pathname.startsWith(c.to),
  );
  const [open, setOpen] = useState(hasActiveChild);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={
          "flex items-center gap-2.5 w-full px-3 py-2 rounded-lg text-sm font-medium transition-colors " +
          (hasActiveChild
            ? "text-primary"
            : "text-foreground hover:bg-accent hover:text-accent-foreground")
        }
      >
        <group.icon className="w-4 h-4 shrink-0" aria-hidden="true" />
        <span className="flex-1 text-left">{group.label}</span>
        <ChevronDown
          className={
            "w-4 h-4 shrink-0 transition-transform " + (open ? "" : "-rotate-90")
          }
          aria-hidden="true"
        />
      </button>
      {open && (
        <div className="mt-1 ml-4 pl-2 border-l border-border flex flex-col gap-1">
          {group.children.map((child) => (
            <NavLink key={child.to} to={child.to} className={leafLinkClass}>
              <span className="flex-1">{child.label}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

// Optional link back to the main Atlas Copilot (member-360) web app. When set,
// the header shows a "← Back to app" link; when unset (e.g. local dev), it's
// hidden. This standalone admin app has no /members route of its own.
const MEMBER_360_APP_URL = import.meta.env.VITE_MEMBER_360_APP_URL as
  | string
  | undefined;

export default function AdminLayout() {
  const { logout } = useAuthContext();
  const { isDark, toggleTheme } = useDarkMode();
  const location = useLocation();

  // Emit admin_page_viewed on every admin navigation (pathname change).
  // The per-pathname ref collapses StrictMode's double-invoke into a single
  // emission per real navigation while still re-firing on every route change
  // (unlike a module-level flag that would fire only once per tab lifetime).
  const lastTrackedPath = useRef<string | null>(null);
  useEffect(() => {
    if (lastTrackedPath.current !== location.pathname) {
      lastTrackedPath.current = location.pathname;
      trackEvent("admin_page_viewed", { page: location.pathname });
    }
  }, [location.pathname]);

  return (
    // `h-full` (not `min-h-full`): this needs to be a DEFINITE height so the
    // `flex-1 min-h-0` row below actually caps at the viewport instead of
    // growing with its content. With only a min-height, the column had no
    // ceiling — the whole document grew taller than the viewport and the
    // page itself scrolled to reach the sidebar's sign-out button and the
    // table's pagination, instead of just the `<main>` content scrolling.
    <div className="h-full flex flex-col font-sans text-foreground bg-background">
      {/* Top header */}
      <header
        className="flex items-center justify-between gap-4 py-2.5 bg-card border-b border-border shadow-sm shrink-0 px-6"
      >
        <div className="flex items-center gap-2">
          <div
            style={{
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 9999,
              background: isDark
                ? "linear-gradient(to bottom, rgba(198, 107, 162, 0.25), transparent)"
                : "linear-gradient(to bottom, rgba(108, 12, 70, 0.12), transparent)",
            }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M12 2L2 19h20L12 2z"
                fill={isDark ? "hsl(340,45%,60%)" : "hsl(324,80%,24%)"}
                opacity="0.85"
              />
            </svg>
          </div>
          <h1 className="m-0 text-lg font-bold bg-gradient-to-r from-primary to-[hsl(324,60%,45%)] dark:from-[hsl(324,60%,70%)] dark:to-[hsl(324,80%,82%)] bg-clip-text text-transparent">
            Atlas Copilot Admin
          </h1>
          {MEMBER_360_APP_URL && (
            <a
              href={MEMBER_360_APP_URL}
              className="ml-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              ← Back to app
            </a>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
            className="w-7 h-7 rounded-md flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            {isDark ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            )}
          </button>
        </div>
      </header>

      <div className="flex flex-1 min-h-0">
        {/* Nav — vertical sidebar. */}
        <nav
          className="w-52 shrink-0 border-r border-border bg-card flex flex-col py-4 gap-1 px-2 overflow-y-auto"
        >
          {NAV_ITEMS.map((item) =>
            isGroup(item) ? (
              <NavGroupSection key={item.label} group={item} />
            ) : (
              <NavLink key={item.to} to={item.to} className={leafLinkClass}>
                <item.icon className="w-4 h-4 shrink-0" aria-hidden="true" />
                <span className="flex-1">{item.label}</span>
              </NavLink>
            ),
          )}
          <div className="mt-auto pt-4 border-t border-border mx-1">
            <button
              type="button"
              onClick={() => void logout()}
              className="flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <LogOut className="w-4 h-4" aria-hidden="true" />
              Sign out
            </button>
          </div>
        </nav>

        {/* Page content */}
        <main className="flex-1 min-h-0 overflow-auto p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
